import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

// Heavy, expensive spring — mass and damping, never bouncy.
export const SPRING = { type: "spring", stiffness: 66, damping: 19, mass: 1.05 };

export const reveal = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: SPRING },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11 } },
};

const viewport = { once: true, margin: "-10% 0px -10% 0px" };

export function Reveal({ children, className, delay = 0, as = "div" }) {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ ...SPRING, delay }}
    >
      {children}
    </Comp>
  );
}

// Clip-masked line reveal for display headings. The viewport trigger lives on
// the OUTER mask (always visible to IntersectionObserver); the clipped inner
// line inherits the variant — a fully-clipped element never reports as
// intersecting, so observing the inner span directly would never fire.
export function MaskReveal({ children, className = "", delay = 0 }) {
  return (
    <motion.span
      className={`block overflow-hidden pb-[0.12em] ${className}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-12% 0px" }}
    >
      <motion.span
        className="block"
        variants={{
          hidden: { y: "112%" },
          show: {
            y: "0%",
            transition: { type: "spring", stiffness: 74, damping: 21, mass: 1, delay },
          },
        }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

export function Stagger({ children, className }) {
  return (
    <motion.div
      className={className}
      variants={stagger}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }) {
  return (
    <motion.div className={className} variants={reveal}>
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, className = "" }) {
  return <span className={`eyebrow ${className}`}>{children}</span>;
}

// Section number: numeral slides up through a mask, rule draws itself.
// Same clipped-reveal rule as MaskReveal: the observer sits on the mask.
export function SectionNo({ n, label }) {
  return (
    <div className="flex items-center gap-3">
      <motion.span
        className="block overflow-hidden"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <motion.span
          className="block font-mono text-[11px] tracking-widest text-gold"
          variants={{
            hidden: { y: "130%" },
            show: {
              y: "0%",
              transition: { type: "spring", stiffness: 92, damping: 20 },
            },
          }}
        >
          {n}
        </motion.span>
      </motion.span>
      <motion.span
        className="h-px w-8 origin-left bg-gold/30"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      />
      <span className="eyebrow">{label}</span>
    </div>
  );
}

// Hairlines draw themselves.
export function Hairline({ className = "" }) {
  return (
    <motion.div
      className={`hairline origin-left ${className}`}
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "-6% 0px" }}
      transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
      aria-hidden="true"
    />
  );
}

// PlaceholderTag was a visible marker for values that must be replaced
// before launch. Now that copy has settled with real values, it renders
// nothing. Left in place so upstream JSX doesn't need to change.
export function PlaceholderTag() {
  return null;
}

// Buttons lean gently toward the cursor. No-op on touch devices.
export function Magnetic({ children, strength = 0.28, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className={`inline-block ${className}`}
      style={{ x: sx, y: sy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
}
